import { LANGUAGE_CN } from '@intelligence/_config/config';
import { parseReportOriginalFilePaths } from '@home/utils/reportPreview';

// 报告system_id与情报报告列表页面path的映射表
const REPORT_LIST_PAGE_PATH_MAPPING: Record<string, string> = {
  intelligence_ai: '/intelligence/reports/aiHubReports',
  intelligence: '/intelligence/reports/marketReports',
  intelligence_insights: '/intelligence/reports/insightsReports',
  intelligence_iic: '/intelligence/reports/insightsCenterLibrary',
  game_analysis: '/intelligence/reports/GameAnalysis',
  gameResearch: '/intelligence/gameResearchReport',
};

// MarketReport报告数据源与情报MarketReport报告列表列表页面查询参数的映射表
const MARKET_REPORT_LIST_PAGE_TAB_MAPPING: Record<string, { tab: string }> = {
  newzoo: { tab: 'newzoo' },
  'sensor tower': { tab: 'sensortower' },
  appmagic: { tab: 'appmagic' },
  ampere: { tab: 'Ampere' },
  vgi: { tab: 'VGI' },
  'data.ai': { tab: 'data.ai' },
  'stream hatchet': { tab: 'streamhatchet' },
  npd: { tab: 'npd' },
  statista: { tab: 'statista' },
  famitsu: { tab: 'famitsu' },
  twitter: { tab: 'twitter' },
  fandom: { tab: 'fandom' },
  others: { tab: 'others' },
};

/**
 * 获取指定system_id的报告 对应的 情报的报告列表页面path
 * @param reportSystemId 指定报告的system_id
 * @return 指定报告对应的情报的报告列表页面path
 */
const getDBIReportListPagePathname = (reportSystemId?: string): string => {
  if (!reportSystemId) return '';
  return REPORT_LIST_PAGE_PATH_MAPPING[reportSystemId] || '';
};

/**
 * 获取指定数据源报告 对应的 情报MarketReports列表页面查询参数对象
 * @param reportSource 报告的数据源
 * @return 报告对应的情报MarketReports报告列表页的查询参数对象
 */
const getDBIMarketReportListPageSearchParams = (reportSource?: string): Record<string, string> => {
  if (!reportSource) return MARKET_REPORT_LIST_PAGE_TAB_MAPPING.others;
  return MARKET_REPORT_LIST_PAGE_TAB_MAPPING[reportSource.toLowerCase()] || MARKET_REPORT_LIST_PAGE_TAB_MAPPING.others;
};

/**
 * 获取指定system_id的报告 对应的 情报的报告列表页面path
 * @param reportSystemId 指定报告的system_id
 * @param reportSource 报告的数据源
 * @return 指定报告对应的情报的报告列表页面path
 */
export const getDBIReportListPagePath = (reportSystemId?: string, reportSource?: string): string => {
  if (!reportSystemId) return '';
  const pathname = getDBIReportListPagePathname(reportSystemId);
  if (!pathname) return '';
  if (reportSystemId !== 'intelligence') return pathname;

  const searchParams = new URLSearchParams(getDBIMarketReportListPageSearchParams(reportSource));
  const search = searchParams.toString();

  return !search ? pathname : `${pathname}?${search}`;
};

/**
 * 获取报告的COS存储path
 * @param locale 当前语言环境
 * @param report 报告
 * @return 报告的COS存储path
 */
export const getReportResourcePath = (
  locale: string,
  report?: {
    lang?: 'en' | 'cn' | (string & {});
    pdf?: string;
    pdf_en?: string;
  },
): string => {
  const { lang, pdf: filePathCN, pdf_en: filePathEN } = report || {};
  const filePathDefault = filePathEN || filePathCN || '';

  if (lang === 'en') {
    return filePathEN || filePathDefault;
  }

  if (lang === 'cn') {
    return filePathCN || filePathDefault;
  }

  return (locale === LANGUAGE_CN ? filePathCN : filePathEN) || filePathDefault;
};

/**
 * 获取指定语种报告原始文件的 COS 存储 path
 */
export const getReportOriginalResourcePath = (
  locale: string,
  report?: {
    lang?: 'en' | 'cn' | (string & {});
    original_file_name?: string;
  },
): string => {
  const { lang, original_file_name: originalFileName } = report || {};
  const { filePathCN, filePathEN } = parseReportOriginalFilePaths(originalFileName);
  return getReportResourcePath(locale, {
    lang,
    pdf: filePathCN,
    pdf_en: filePathEN,
  });
};
